// src/pages/core-info/CreateMeterPage.tsx
import { useState, useEffect, useRef, useCallback } from 'react';
import { Form, Row, Col, Card, Alert, Input, Select, Spin, App } from 'antd';
import NSPDatePicker from '@components/ui/NSPDatepicker';
import { useNavigate, useLocation } from 'react-router-dom';
import { useMutation, useLazyQuery } from '@apollo/client';
import { CREATE_METER_CORE_INFO, GET_IPP_BY_VENDOR_NAME } from '@graphql/meterCoreInfo.gql';
import { CREATE_IPP_VENDOR } from '@graphql/vendorList.gql';
import { NSPButton } from '@components/ui/NSPButton';
import CoreInfoMeterSourceInfoSection from '@components/core-info/meter-source-info-section/CoreInfoMeterSourceInfoSectionGraphQl';
import CoreInfoTariffContractInfoSection from '@components/core-info/tariff-contract-info-section/CoreInfoTariffContractInfoSectionGraphQl';
import PageLayout from '@components/layout/PageLayout';
import styles from './CreateMeterPage.module.css';
import dayjs from 'dayjs';

const AntCard = Card as unknown as React.ComponentType<any>;

const EMPTY_RATE_CONTRACTS = [
    { rateType: 'CONTRACT', rateId: undefined, effectiveFrom: undefined, effectiveTo: undefined, rate: undefined, rateDescription: undefined, isPlaceholder: true },
    { rateType: 'RETROACTIVE', rateId: undefined, effectiveFrom: undefined, effectiveTo: undefined, rate: undefined, rateDescription: undefined, isPlaceholder: true },
    { rateType: 'OVERAGE', rateId: undefined, effectiveFrom: undefined, effectiveTo: undefined, rate: undefined, rateDescription: undefined, isPlaceholder: true },
    { rateType: 'MAX_CAPACITY', rateId: undefined, effectiveFrom: undefined, effectiveTo: undefined, rate: undefined, rateDescription: undefined, isPlaceholder: true },
];

function CreateMeterPageInner() {
    const [form] = Form.useForm();
    const navigate = useNavigate();
    const location = useLocation();
    const { modal, message } = App.useApp();
    const prefillIpp = (location.state as any)?.prefill?.ipp;
    const isVendorPrefilled = !!prefillIpp?.ippId;

    const [saving, setSaving] = useState(false);
    const [errorMsg, setErrorMsg] = useState<string | null>(null);
    const [saveSuccess, setSaveSuccess] = useState(false);
    const [ippLookupLoading, setIppLookupLoading] = useState(false);

    const resolvedIppIdRef = useRef<number | null | undefined>(undefined);

    const [createVendor] = useMutation(CREATE_IPP_VENDOR);
    const [createMeter] = useMutation(CREATE_METER_CORE_INFO, {
        onError: (err) => {
            setErrorMsg(err.message || 'An unexpected error occurred. Please try again.');
            setSaving(false);
        },
    });

    useEffect(() => {
        form.setFieldsValue({ rateContracts: EMPTY_RATE_CONTRACTS });

        if (prefillIpp) {
            const { ippId, vendorName, needDetailsWithInvoice } = prefillIpp;


            form.setFieldsValue({
                ipp: {
                    vendorName: vendorName ?? '',
                    needDetailsWithInvoice: needDetailsWithInvoice ?? undefined,
                },
            });

            // ippId is already known — skip DB lookup on save
            if (ippId) {
                resolvedIppIdRef.current = Number(ippId);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); // intentionally mount-only

    // ── Vendor lookup (manual entry path) ────────────────────────────────────
    const [fetchIppByVendor] = useLazyQuery(GET_IPP_BY_VENDOR_NAME, {
        fetchPolicy: 'network-only',
        onCompleted: (data) => {
            const ipp = data?.ippByVendorName;
            resolvedIppIdRef.current = ipp?.ippId ?? null;
            setIppLookupLoading(false);
        },
        onError: () => {
            resolvedIppIdRef.current = null;
            setIppLookupLoading(false);
        },
    });

    const handleVendorBlur = useCallback(() => {
        // When the field is pre-filled and locked, no lookup is needed
        if (isVendorPrefilled) return;

        const vendorName = form.getFieldValue(['ipp', 'vendorName'])?.trim();
        if (!vendorName) return;

        resolvedIppIdRef.current = undefined;
        setIppLookupLoading(true);
        fetchIppByVendor({ variables: { vendorName } });
    }, [form, fetchIppByVendor, isVendorPrefilled]);

    // Trigger lookup imperatively when user clicks Save without blurring first
    const lookupVendorNow = useCallback((vendorName: string): Promise<number | null> => {
        return new Promise((resolve) => {
            resolvedIppIdRef.current = undefined;
            setIppLookupLoading(true);
            fetchIppByVendor({ variables: { vendorName } });

            const interval = setInterval(() => {
                if (resolvedIppIdRef.current !== undefined) {
                    clearInterval(interval);
                    resolve(resolvedIppIdRef.current);
                }
            }, 50);

            // Safety timeout
            setTimeout(() => {
                clearInterval(interval);
                resolve(resolvedIppIdRef.current ?? null);
            }, 10000);
        });
    }, [fetchIppByVendor]);

    const normalizeDate = (val: any): string | null => {
        if (!val) return null;
        const d = dayjs(val);
        return d.isValid() ? d.format('YYYY-MM-01') : null;
    };

    const executeSave = async (values: any, overrideIppId?: number | null) => {
        setSaving(true);

        const ippId = overrideIppId !== undefined ? overrideIppId : resolvedIppIdRef.current;

        const rateContractsInput = (values.rateContracts || []).map((rc: any) => ({
            rateType: rc.rateType,
            effectiveFrom: normalizeDate(rc.effectiveFrom),
            effectiveTo: normalizeDate(rc.effectiveTo),
            rate: rc.rate != null ? Number(rc.rate) : null,
            rateDescription: rc.rateDescription || null,
        }));

        const input = {
            meterNumber: values.meterNumber || null,
            meterName: values.meterName || null,
            energy: values.energy || null,
            region: values.region || null,
            meterType: values.meterType || null,
            piTagNameArgControlStatus: values.piTagNameArgControlStatus || null,
            piTagNameMegawattValue: values.piTagNameMegawattValue || null,
            lossFactor: values.lossFactor != null ? Number(values.lossFactor) : null,
            multiplier: values.multiplier != null ? Number(values.multiplier) : null,
            statGl: values.statGl?.glNumber ? { glNumber: values.statGl.glNumber } : null,
            dollarGl: values.dollarGl?.glNumber ? { glNumber: values.dollarGl.glNumber } : null,
            poLineNumber: values.poLineNumber != null && values.poLineNumber !== ''
                ? Number(values.poLineNumber)
                : null,
            ipp: ippId
                ? {
                    ippId,
                    vendorName: values.ipp?.vendorName || null,
                    needDetailsWithInvoice: values.ipp?.needDetailsWithInvoice || null,
                }
                : null,
            meterFileSource: values.meterFileSource || null,
            kwhGenerated: values.kwhGenerated || null,
            kwhUsed: values.kwhUsed || null,
            energyBid: values.energyBid != null ? Number(values.energyBid) : null,
            retroactiveReminder: normalizeDate(values.retroactiveReminder),
            selectedRateType: values.selectedRateType || null,
            rateContracts: rateContractsInput,
            meterSwap: values.meterSwap || null,
            finalMeterRead: values.final_meter_read != null ? Number(values.final_meter_read) : null,
            finalReadDate: values.final_read_date ? dayjs(values.final_read_date).toISOString() : null,
            operationalStatus: values.operationalStatus || null,
            operationalStatusDate: values.operationalStatusDate?.isValid()
                ? values.operationalStatusDate.format('YYYY-MM-DD')
                : null,
            adminFeeRate: values.adminFeeRate != null && values.adminFeeRate !== '' ? Number(values.adminFeeRate) : null,
            forecastFee: values.forecastFee != null && values.forecastFee !== '' ? Number(values.forecastFee) : null,
            forecastFeeCurrency: values.forecastFeeCurrency || null,
            forecastFeeGl: values.forecastFeeGl?.glNumber ? { glNumber: values.forecastFeeGl.glNumber } : null,
            adminFeeGl: values.adminFeeGl?.glNumber ? { glNumber: values.adminFeeGl.glNumber } : null,
        };

        try {
            const result = await createMeter({ variables: { input } });
            if (result?.data) {
                const newMeterId = result.data.createMeterCoreInfo?.meterId;
                const meterNumber = result.data.createMeterCoreInfo?.meterNumber;
                setSaveSuccess(true);
                message.success(`Meter${meterNumber ? ' ' + meterNumber : ''} created successfully`, 1.5);
                setTimeout(() => {
                    navigate(newMeterId ? `/core-info/${newMeterId}` : '/');
                }, 1500);
            }
        } finally {
            setSaving(false);
        }
    };

    const handleSave = async () => {
        setErrorMsg(null);
        setSaveSuccess(false);

        // Step 1: validate all fields
        let values: any;
        try {
            values = await form.validateFields();
        } catch (validationError: any) {
            const failedFields = (validationError?.errorFields ?? [])
                .map((f: any) => `• ${f.name.join(' > ')}: ${f.errors.join(', ')}`)
                .join('\n');
            if (failedFields) {
                setErrorMsg(`Please fix the following before saving:\n${failedFields}`);
            }
            return;
        }

        const vendorName = values.ipp?.vendorName?.trim();

        if (!vendorName) {
            await executeSave(values);
            return;
        }

        if (ippLookupLoading) {
            message.info('Please wait for vendor lookup to complete…');
            return;
        }

        // Step 2: if pre-filled, ippId is already in ref — skip everything below
        let resolvedId = resolvedIppIdRef.current;

        if (resolvedId === undefined) {
            message.loading({ content: 'Looking up vendor…', key: 'vendor-lookup' });
            resolvedId = await lookupVendorNow(vendorName);
            message.destroy('vendor-lookup');
        }

        // Step 3: vendor found
        if (resolvedId !== null && resolvedId !== undefined) {
            await executeSave(values, resolvedId);
            return;
        }

        // Step 4: vendor not found — ask user
        modal.confirm({
            title: 'Vendor Not Found',
            content: (
                <span>
                    The vendor <strong>"{vendorName}"</strong> was not found in the database.
                    If you proceed, a new vendor will be created with this name and the meter will be saved.
                    <br /><br />
                    Click <strong>No, go back</strong> to correct the vendor name.
                </span>
            ),
            okText: 'Yes, create vendor & save',
            cancelText: 'No, go back',
            onOk: async () => {
                try {
                    const vendorResult = await createVendor({ variables: { input: { vendorName } } });
                    const newIppId = vendorResult.data?.createIppVendor?.id;
                    if (!newIppId) {
                        setErrorMsg(`Failed to create vendor "${vendorName}". Please try again.`);
                        throw new Error('No ippId returned');
                    }
                    resolvedIppIdRef.current = newIppId;
                    await executeSave(values, newIppId);
                } catch (err: any) {
                    setErrorMsg(err?.message || 'An error occurred while creating the vendor.');
                    throw err;
                }
            },
        });
    };

    const handleCancel = () => {
        if (form.isFieldsTouched()) {
            modal.confirm({
                title: 'Discard changes?',
                content: 'Are you sure you want to cancel and lose your changes?',
                onOk: () => navigate(-1),
            });
        } else {
            navigate(-1);
        }
    };

    return (
        <PageLayout title="Add New Meter">
        <div className={styles.pageWrapper}>



            {errorMsg && (
                <Alert
                    message="Cannot save"
                    description={<pre style={{ margin: 0, whiteSpace: 'pre-wrap', fontFamily: 'inherit' }}>{errorMsg}</pre>}
                    type="error"
                    showIcon
                    closable
                    onClose={() => setErrorMsg(null)}
                    className={styles.errorAlert}
                />
            )}

            {saveSuccess && (
                <Alert
                    message="Meter created successfully"
                    description="Redirecting to the meter page…"
                    type="success"
                    showIcon
                    className={styles.successAlert}
                />
            )}

            <Form
                form={form}
                layout="vertical"
                className={styles.formRoot}
                scrollToFirstError
                initialValues={{
                    meterNumber: '',
                    meterName: '',
                    meterType: undefined,
                    ipp: { vendorName: '', purchaseOrderNo: '', needDetailsWithInvoice: undefined },
                    energy: '',
                    region: '',
                    statGl: { glNumber: '' },
                    dollarGl: { glNumber: '' },
                    meterFileSource: '',
                    kwhGenerated: '',
                    kwhUsed: '',
                    rateContracts: EMPTY_RATE_CONTRACTS,
                    selectedRateId: undefined,
                    retroactiveReminder: undefined,
                    selectedRateType: undefined,
                    energyBid: '',
                    operationalStatus: 'Active',
                    operationalStatusDate: undefined,
                    forecastFee: undefined,
                    forecastFeeCurrency: undefined,
                    forecastFeeGl: { glNumber: '' },
                    adminFeeRate: undefined,
                    adminFeeGl: { glNumber: '' },
                }}
            >
                <Row gutter={24} align="stretch">
                    <Col xs={24} md={8}>
                        <AntCard title="General Info" variant="contained" className={styles.cardSection}>
                            <Row gutter={16}>
                                <Col span={12}>
                                    <Form.Item label="Meter Number" name="meterNumber" rules={[{ required: true, message: 'Meter number is required' }]}>
                                        <Input />
                                    </Form.Item>
                                </Col>
                                <Col span={12}>
                                    <Form.Item label="Location" name="meterName" rules={[{ required: true, message: 'Location is required' }]}>
                                        <Input />
                                    </Form.Item>
                                </Col>
                            </Row>

                            <Row gutter={16}>
                                <Col span={12}>
                                    <Form.Item label="Vendor Name" name={['ipp', 'vendorName']} rules={[{ required: true, message: 'Vendor Name is required' }]}>
                                        {/*
                      Disabled when prefilled (vendor is locked to the one from VendorForm).
                      When enabled, onBlur triggers a DB lookup.
                    */}
                                        <Input
                                            disabled={isVendorPrefilled}
                                            onBlur={handleVendorBlur}
                                            suffix={
                                                // Always rendered to avoid DOM restructure focus-loss warning
                                                <Spin size="small" style={{ visibility: ippLookupLoading ? 'visible' : 'hidden' }} />
                                            }
                                        />
                                    </Form.Item>
                                </Col>
                                <Col span={12}>
                                    <Form.Item label="Energy" name="energy" rules={[{ required: true, message: 'Energy type is required' }]}>
                                        <Select options={[{ value: 'Wind', label: 'Wind' }, { value: 'Solar', label: 'Solar' }]} />
                                    </Form.Item>
                                </Col>
                            </Row>

                            <Row gutter={16}>
                                <Col span={12}>
                                    <Form.Item label="Region" name="region" rules={[{ required: true, message: 'Region is required' }]}>
                                        <Input />
                                    </Form.Item>
                                </Col>
                                <Col span={12}>

                                    <Form.Item label="PO Line Number" name="poLineNumber">
                                        <Input placeholder="Enter PO number" />
                                    </Form.Item>
                                </Col>
                            </Row>

                            <Row gutter={16}>
                                <Col span={12}>
                                    <Form.Item label="Stat GL" name={['statGl', 'glNumber']} rules={[{ required: true, message: 'Stat GL is required' }]}>
                                        <Input />
                                    </Form.Item>
                                </Col>
                                <Col span={12}>
                                    <Form.Item label="Dollar GL" name={['dollarGl', 'glNumber']} rules={[{ required: true, message: 'Dollar GL is required' }]}>
                                        <Input />
                                    </Form.Item>
                                </Col>
                            </Row>

                            <Row gutter={16}>
                                <Col span={24}>

                                    <Form.Item label="Invoice to include meter read details?" name={['ipp', 'needDetailsWithInvoice']}>
                                        <Select allowClear options={[{ value: 'Yes', label: 'Yes' }, { value: 'No', label: 'No' }]} />
                                    </Form.Item>
                                </Col>
                            </Row>

                            <Row gutter={16}>
                                <Col span={24}>
                                    <Form.Item label="IPP Type?" name="meterType" rules={[{ required: true, message: 'IPP Type is required' }]}>
                                        <Select allowClear placeholder="Select IPP type" options={[
                                            { value: 'IPP', label: 'IPP' },
                                            { value: 'COMFIT', label: 'COMFIT' },
                                            { value: 'AGC Wind Farm', label: 'AGC Wind Farm' },
                                            { value: 'Community Solar', label: 'Community Solar' },
                                        ]} />
                                    </Form.Item>
                                </Col>
                            </Row>

                            <Row gutter={16}>
                                <Col span={12}>
                                    <Form.Item label="Operational Status" name="operationalStatus">
                                        <Select
                                            placeholder="Select status"
                                            options={[
                                                { value: 'Active', label: 'Active' },
                                                { value: 'Inactive', label: 'Inactive' },
                                            ]}
                                        />
                                    </Form.Item>
                                </Col>
                                <Col span={12}>
                                    <Form.Item label="Date of Status" name="operationalStatusDate">
                                        <NSPDatePicker style={{ width: '100%' }} />
                                    </Form.Item>
                                </Col>
                            </Row>
                        </AntCard>

                        <AntCard title="Meter Source Info" variant="contained" className={styles.cardSection}>
                            <CoreInfoMeterSourceInfoSection form={form} />
                        </AntCard>
                    </Col>

                    <Col xs={24} md={16}>
                        <AntCard title="Tariff / Contract Info" variant="contained" className={styles.cardSection}>
                            <CoreInfoTariffContractInfoSection form={form} />
                        </AntCard>
                    </Col>
                </Row>

                <Row justify="end" gutter={12} className={styles.actionRow}>
                    <Col>
                        <NSPButton danger disabled={saving || saveSuccess} onClick={handleCancel} data-testid="cancel-button">
                            Cancel
                        </NSPButton>
                    </Col>
                    <Col>
                        <NSPButton loading={saving} disabled={saveSuccess} onClick={handleSave} data-testid="save-button">
                            Save
                        </NSPButton>
                    </Col>
                </Row>
            </Form>
        </div>
        </PageLayout>
    );
}

export default function CreateMeterPage() {
    return (
        <App>
            <CreateMeterPageInner />
        </App>
    );
}